<template>
  <WdsStack
    direction-column
    justify-space-between
    class="tw-h-full edit-group-modal"
    data-test="workflow-meta-editor"
  >
    <WdsStack direction-column gap-md class="tw-mb-5">
      <WdsSelect
        v-model="trigger"
        :disabled="editMode"
        class="type-input"
        :list="triggerOptions"
        name="workflow-trigger-select"
      >
        <template #prefix>
          <WdsStack v-if="trigger" class="icon-highlight" align-center justify-center>
            <WdsIcon :name="trigger.icon" :size="16" weight="thin" color="#5D36F1" />
          </WdsStack>
        </template>
        <template #label>
          <WdsText>
            {{ $t('Workflow type') }}
          </WdsText>
        </template>
        <template #option="{ option }">
          <WdsStack gap-sm>
            <WdsStack class="icon-highlight" align-center justify-center>
              <WdsIcon :name="option.icon" :size="16" weight="thin" color="#5D36F1" />
            </WdsStack>
            <span>{{ option.label }}</span>
          </WdsStack>
        </template>
      </WdsSelect>
      <WdsInput
        v-model="name"
        data-test="workflow-name-input"
        name="name"
        :placeholder="$t('New workflow')"
        :error="nameError || backEndErrors['input.root.data.name'] || null"
        :disabled="submitLoading"
      >
        <template #label>
          <WdsText>
            {{ $t('Workflow name') }}
          </WdsText>
        </template>
      </WdsInput>
      <WdsInputArea
        v-model="description"
        data-test="workflow-description-text"
        :disabled="submitLoading"
        :placeholder="$t('Enter description...')"
        :error="descriptionError || backEndErrors['input.root.data.description'] || null"
      >
        <template #label>
          <WdsStack gap-sm justify-space-between>
            <WdsStack gap-sm>
              <WdsText>
                {{ $t('Workflow description') }}
              </WdsText>
              <div>
                <WdsTag sm>
                  {{ $t('Optional') }}
                </WdsTag>
              </div>
            </WdsStack>
          </WdsStack>
        </template>
      </WdsInputArea>
    </WdsStack>
    <WdsStack gap-sm justify-space-between align-center>
      <WdsButton @click="emit('cancel')">{{ $t('Cancel') }}</WdsButton>
      <WdsStack gap-sm align-center>
        <WdsSpinner v-if="submitLoading" indeterminate />

        <WdsTooltip v-if="quietErrors.length > 0">
          <WdsButton data-test="workflow-save-button" primary @click="submitForm">
            {{ editMode ? $t('Update') : $t('Save') }}
          </WdsButton>
          <template #tooltip-content>
            <span v-for="e in quietErrors" :key="e">
              {{ e }}
              <br />
            </span>
          </template>
        </WdsTooltip>
        <WdsButton
          v-else
          data-test="workflow-save-button"
          primary
          :disabled="submitLoading"
          @click="submitForm"
        >
          {{ editMode ? $t('Update') : $t('Save') }}
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import {
  WdsButton,
  WdsIcon,
  WdsInput,
  WdsInputArea,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { useMutation } from '@vue/apollo-composable'
import { computed, Ref, reactive, ref, unref, PropType } from 'vue'
import { useVModel } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import { Workflow } from '@/components/Workflows/Pages/Workflow/workflow'
import { uuid } from '@/utils/tools'
import { ApprovalStatus, ApprovalTrigger } from '@/gql/graphql'
import { triggerOptions } from '@/components/Workflows/utils'

const emit = defineEmits(['submitted', 'submitting', 'error', 'cancel', 'update:modelValue'])

const props = defineProps({
  modelValue: {
    type: Object as PropType<Workflow>,
    default: () => ({
      id: 'fake',
      name: '',
      description: null,
      status: ApprovalStatus.Inactive,
      createdAt: '',
      updatedAt: '',
      approvalRules: [],
    }),
  },
})

// Not the best check but will do for now
const editMode = props.modelValue.id !== 'fake'

const clientId = 'publicApiCustomFields'

const workflow: Ref<Workflow> = reactive(useVModel(props, 'modelValue', emit)) as Ref<Workflow>

const backEndErrors = reactive({})

const name = computed({
  get: () => workflow.value?.name ?? '',
  set: (val: string) => {
    workflow.value.name = val
  },
})
const description = computed({
  get: () => workflow.value?.description ?? '',
  set: (val: string) => {
    workflow.value.description = val
  },
})
const company = ref(currentAccount.globalId)

const selectedTrigger = triggerOptions.find((option) => {
  const selected = workflow.value.trigger ?? ApprovalTrigger.HireCreated
  return option.value === selected
})
const trigger = ref(selectedTrigger)
const status = computed(() => workflow.value?.status ?? ApprovalStatus.Inactive)

const state = computed(() => {
  const base = {
    name: name.value,
    description: description.value,
    trigger: trigger.value?.value,
    status: status.value,
  }

  if (editMode) {
    return {
      id: workflow.value.id,
      ...base,
    }
  }

  return {
    company: company.value,
    ...base,
  }
})

const {
  mutate: createWorkflow,
  loading: createWorkflowLoading,
  error: createWorkflowError,
} = useMutation(
  gql`
    mutation CreateWorkflow($input: CreateWorkflowInput!) {
      createWorkflow(input: $input) {
        id
      }
    }
  `,
  {
    clientId,
    refetchQueries: ['Workflows'],
  },
)

const submitLoading = computed(() => createWorkflowLoading.value)

const vuelidate = useCustomVuelidate(
  gql`
    input CreateApprovalInput {
      name: String!
      description: String
    }
  `,
  state,
)

const { error, touchAll, quietErrors } = vuelidate

const { name: nameError, description: descriptionError } = useApolloAndVuelidate(
  useApolloError(createWorkflowError),
  vuelidate,
  'input',
)

const submitForm = async () => {
  touchAll()

  if (error.value) return

  if (editMode) {
    emit('submitted')
    return
  }

  try {
    const input = {
      root: {
        id: uuid(),
        children: [uuid()], // Ghost child to pass requirements works apparently
        data: unref(state),
      },
      rules: [],
      approvers: [],
    }
    emit('submitting')

    const response = await createWorkflow({ input })
    emit('submitted', response?.data?.createWorkflow)
  } catch (e) {
    const validationErrorKeys = Object.keys((e as any).graphQLErrors[0].extensions.validation)

    validationErrorKeys.forEach((key) => {
      backEndErrors[key] = (e as any).graphQLErrors[0].extensions.validation[key][0]
    })

    emit('error', e)
  }
}
</script>

<style lang="scss" scoped>

.type-input {
  &:deep(.wds-input-with-prefix-icon) {
    padding-left: 42px;
  }

  .icon-highlight {
    background-color: #f3f0ff;
    width: 24px;
    height: 24px;
    align-items: center;
    border-radius: 6px;
  }
}
</style>
